<script setup lang="ts">
// Sign-in for both hosts: a node has one local admin password (set on first
// visit); central has email + password and creates the first superadmin on
// first visit.
import { computed, nextTick, ref, useTemplateRef } from 'vue'
import { ApiError } from '../api'
import { useNode } from '../store'
import Brand from './Brand.vue'

const node = useNode()
const email = ref('')
const pw = ref('')
const pw2 = ref('')
const token = ref('')
// two-factor (central): asked for once the password was right
const needCode = ref(false)
const code = ref('')
const codeInput = useTemplateRef<HTMLInputElement>('codeInput')
// a single sign-on that went wrong comes back here with why (central sso.go)
const err = ref(new URLSearchParams(location.search).get('sso_error') ?? '')
if (err.value) history.replaceState(null, '', location.pathname) // not again on a reload
const busy = ref(false)
const setup = computed(() => (node.central ? node.setupRequired : !node.passwordSet))
const title = computed(() =>
    node.central
        ? setup.value
            ? 'Create the first administrator'
            : 'Sign in to central'
        : setup.value
          ? 'Set the admin password'
          : 'Sign in',
)

async function go() {
    err.value = ''
    if (setup.value && pw.value !== pw2.value) {
        err.value = 'Passwords do not match'
        return
    }
    if (setup.value && pw.value.length < 8) {
        err.value = 'Use at least 8 characters'
        return
    }
    busy.value = true
    try {
        await node.login(pw.value, email.value, token.value.trim(), code.value.trim())
    } catch (e) {
        if (e instanceof ApiError && e.totp) {
            const first = !needCode.value
            needCode.value = true
            code.value = ''
            err.value = e.totp === 'wrong' ? 'That code is not right: try the next one' : ''
            if (first) await nextTick()
            codeInput.value?.focus()
        } else {
            err.value = e instanceof Error ? e.message : 'Login failed'
        }
    } finally {
        busy.value = false
    }
}
</script>

<template>
    <div class="flex h-full items-center justify-center">
        <form class="w-90 max-w-[calc(100vw-2rem)] card px-6 py-6 shadow-2xl shadow-black/30" @submit.prevent="go">
            <Brand full :sub="node.central ? 'Central' : 'Node'" />
            <h1 class="mt-5 mb-3 text-[17px] font-semibold">{{ title }}</h1>
            <p v-if="setup && !node.central" class="text-fg-2">
                This node has no password yet. The password you set here protects the local interface on the venue
                network.
            </p>
            <p v-if="setup && node.central" class="text-fg-2">
                This central has no users yet. The account you create here is the superadmin; add users and groups
                afterwards.
            </p>
            <div v-if="setup && node.central && node.setupToken" class="mb-2.5">
                <label>Setup token</label
                ><input v-model="token" autocomplete="off" spellcheck="false" placeholder="setup_…" class="font-mono" />
                <div class="mt-1 text-xs text-fg-3">
                    In central's log since it started:
                    <code class="font-mono">docker compose logs centrald | grep setup_token</code>, or make another:
                    <code class="font-mono">docker compose exec centrald centrald -setup-token</code>
                </div>
            </div>
            <div v-if="node.central" class="mb-2.5">
                <label>Email</label><input v-model="email" type="email" autofocus autocomplete="username" />
            </div>
            <div class="mb-2.5">
                <label>Password</label
                ><input
                    v-model="pw"
                    type="password"
                    :autofocus="!node.central"
                    :autocomplete="setup ? 'new-password' : 'current-password'"
                />
            </div>
            <div v-if="setup" class="mb-2.5">
                <label>Repeat password</label><input v-model="pw2" type="password" autocomplete="new-password" />
            </div>
            <div v-if="needCode" class="mb-2.5">
                <label>Code</label
                ><input
                    ref="codeInput"
                    v-model="code"
                    autocomplete="one-time-code"
                    inputmode="numeric"
                    spellcheck="false"
                    class="font-mono"
                    placeholder="123456"
                    data-test="totp-code"
                />
                <div class="mt-1 text-xs text-fg-3">
                    From your authenticator app, or one of your recovery codes if you do not have it.
                </div>
            </div>
            <div v-if="err" class="mb-2.5 text-bad" data-test="login-error">{{ err }}</div>
            <button class="w-full btn-primary" type="submit" :disabled="busy">
                {{ setup ? (node.central ? 'Create and sign in' : 'Set password and sign in') : 'Sign in' }}
            </button>
            <template v-if="node.central && !setup && node.sso?.enabled">
                <div class="my-2.5 text-center text-xs text-fg-3">or</div>
                <a
                    class="block w-full rounded-md border border-line-strong bg-bg-2 px-2.5 py-1.25 text-center text-fg no-underline hover:bg-bg-3"
                    href="/api/sso/start"
                    data-test="sso-start"
                    >Sign in with {{ node.sso.label ?? 'single sign-on' }}</a
                >
            </template>
        </form>
    </div>
</template>
